import { useState, useEffect, useRef } from "react";
import { t, font, Button, Input, Panel } from "@realtyflows/ui";
import { getPipelines, createPipeline, updatePipeline, deletePipeline, updatePipelineStages } from "../api";
import ConfirmModal from "../components/ConfirmModal";
import useResponsive from "../hooks/useResponsive";

const PRESET_COLORS = [
  "#00a878", "#e8950a", "#2e7cf6", "#e8503a", "#06B6D4",
  "#8B5CF6", "#F59E0B", "#007d5a", "#EC4899", "#22C55E",
  "#6B7FFF", "#F97316", "#94A3B8", "#E11D48", "#14B8A6",
];

function StageRow({ stage, index, total, onUpdate, onRemove, onMoveUp, onMoveDown, dragHandlers, compact }) {
  const [colorOpen, setColorOpen] = useState(false);

  return (
    <div
      draggable
      {...dragHandlers}
      style={{
        display: "flex", alignItems: "center", gap: compact ? 6 : 8, padding: compact ? "8px 8px" : "8px 10px",
        background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 8,
        marginBottom: 4, cursor: "grab",
      }}
    >
      {/* Drag handle */}
      <svg width="12" height="12" viewBox="0 0 12 12" fill={t.textMuted} style={{ flexShrink: 0, cursor: "grab" }}>
        <circle cx="3.5" cy="2" r="1.2" /><circle cx="8.5" cy="2" r="1.2" />
        <circle cx="3.5" cy="6" r="1.2" /><circle cx="8.5" cy="6" r="1.2" />
        <circle cx="3.5" cy="10" r="1.2" /><circle cx="8.5" cy="10" r="1.2" />
      </svg>

      {/* Color picker */}
      <div style={{ position: "relative" }}>
        <div
          onClick={() => setColorOpen(!colorOpen)}
          style={{
            width: 22, height: 22, borderRadius: 6, background: stage.color || "#00a878",
            cursor: "pointer", border: `2px solid ${t.border}`, flexShrink: 0,
          }}
        />
        {colorOpen && (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setColorOpen(false)} />
            <div style={{
              position: "absolute", top: 28, left: compact ? "auto" : 0, right: compact ? 0 : "auto", zIndex: 100,
              background: t.surface, border: `1.5px solid ${t.border}`, borderRadius: 10,
              padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4, width: 140,
            }}>
              {PRESET_COLORS.map(c => (
                <div key={c} onClick={() => { onUpdate({ ...stage, color: c }); setColorOpen(false); }}
                  style={{
                    width: 22, height: 22, borderRadius: 5, background: c, cursor: "pointer",
                    border: stage.color === c ? "2px solid #fff" : "2px solid transparent",
                    boxShadow: stage.color === c ? `0 0 0 1px ${c}` : "none",
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Stage name input */}
      <input
        value={stage.name}
        onChange={e => onUpdate({ ...stage, name: e.target.value })}
        style={{
          flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
          fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body,
          padding: "4px 0",
        }}
        placeholder="Stage name"
      />

      {/* Closed badge */}
      {stage.is_closed && !compact && (
        <span style={{
          fontSize: 8, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
          background: t.emeraldLight, color: t.emerald, fontFamily: font.mono,
          letterSpacing: 0.5, flexShrink: 0,
        }}>CLOSED</span>
      )}

      {/* Move arrows */}
      <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
        <div onClick={index > 0 ? onMoveUp : undefined}
          style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: index > 0 ? "pointer" : "default", opacity: index > 0 ? 1 : 0.2, borderRadius: 4 }}
          onMouseEnter={e => { if (index > 0) e.currentTarget.style.background = t.surface; }}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill={t.textSecondary}><path d="M5 2L1 7h8z" /></svg>
        </div>
        <div onClick={index < total - 1 ? onMoveDown : undefined}
          style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: index < total - 1 ? "pointer" : "default", opacity: index < total - 1 ? 1 : 0.2, borderRadius: 4 }}
          onMouseEnter={e => { if (index < total - 1) e.currentTarget.style.background = t.surface; }}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill={t.textSecondary}><path d="M5 8L1 3h8z" /></svg>
        </div>
      </div>

      {/* Remove button */}
      <div onClick={onRemove}
        style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 4, flexShrink: 0 }}
        onMouseEnter={e => e.currentTarget.style.background = t.coralLight}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </div>
    </div>
  );
}

export default function PipelineSettings() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [editingStages, setEditingStages] = useState(null); // local copy for editing
  const [editingName, setEditingName] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const nameInputRef = useRef(null);
  const responsive = useResponsive();
  const isCompact = responsive.isCompact; // < 1024px
  const isMobile = responsive.width < 640;

  const fetchPipelines = async () => {
    try {
      const data = await getPipelines();
      setPipelines(data);
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
        setEditingStages(data[0].stages);
        setEditingName(data[0].name);
      }
    } catch (err) {
      setToast({ message: "Failed to load pipelines", variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPipelines(); }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const selected = pipelines.find(p => p.id === selectedId);

  const selectPipeline = (p) => {
    setSelectedId(p.id);
    setEditingStages(p.stages);
    setEditingName(p.name);
  };

  const handleSave = async () => {
    if (!selectedId || !editingStages) return;
    // Validate
    const emptyStage = editingStages.find(s => !s.name.trim());
    if (emptyStage) {
      setToast({ message: "Stage names cannot be empty", variant: "error" });
      return;
    }
    setSaving(true);
    try {
      // Save name if changed
      if (editingName !== selected?.name) {
        await updatePipeline(selectedId, { name: editingName });
      }
      // Save stages
      await updatePipelineStages(selectedId, editingStages);
      setToast({ message: "Pipeline saved", variant: "success" });
      await fetchPipelines();
      // Re-select to refresh
      const refreshed = (await getPipelines()).find(p => p.id === selectedId);
      if (refreshed) {
        setEditingStages(refreshed.stages);
        setEditingName(refreshed.name);
      }
    } catch (err) {
      setToast({ message: err.message || "Failed to save", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleAddStage = () => {
    if (!editingStages) return;
    const closedIdx = editingStages.findIndex(s => s.is_closed);
    const insertAt = closedIdx >= 0 ? closedIdx : editingStages.length;
    const newStage = { name: "", color: PRESET_COLORS[editingStages.length % PRESET_COLORS.length], is_closed: false };
    const updated = [...editingStages];
    updated.splice(insertAt, 0, newStage);
    setEditingStages(updated);
  };

  const handleRemoveStage = (index) => {
    if (!editingStages || editingStages.length <= 1) return;
    setEditingStages(editingStages.filter((_, i) => i !== index));
  };

  const handleUpdateStage = (index, updated) => {
    const arr = [...editingStages];
    arr[index] = updated;
    setEditingStages(arr);
  };

  const handleMoveStage = (from, to) => {
    const arr = [...editingStages];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setEditingStages(arr);
  };

  const handleCreatePipeline = async () => {
    if (!newPipelineName.trim()) return;
    try {
      const result = await createPipeline({
        name: newPipelineName.trim(),
        stages: [
          { name: "New", color: "#00a878" },
          { name: "In Progress", color: "#2e7cf6" },
          { name: "Closed", color: "#007d5a", is_closed: true },
        ],
      });
      setAddingNew(false);
      setNewPipelineName("");
      await fetchPipelines();
      setSelectedId(result.id);
      setEditingStages(result.stages);
      setEditingName(result.name);
      setToast({ message: "Pipeline created", variant: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to create pipeline", variant: "error" });
    }
  };

  const handleDeletePipeline = async (id) => {
    try {
      await deletePipeline(id);
      setConfirmDelete(null);
      if (selectedId === id) {
        setSelectedId(null);
        setEditingStages(null);
        setEditingName("");
      }
      await fetchPipelines();
      setToast({ message: "Pipeline deleted", variant: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to delete", variant: "error" });
      setConfirmDelete(null);
    }
  };

  // Stage drag handlers
  const handleStageDragStart = (index) => (e) => {
    setDragIdx(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleStageDragOver = (index) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === index) return;
    handleMoveStage(dragIdx, index);
    setDragIdx(index);
  };
  const handleStageDragEnd = () => { setDragIdx(null); };

  // Check if there are unsaved changes
  const hasChanges = selected && editingStages && (
    editingName !== selected.name ||
    JSON.stringify(editingStages.map(s => ({ name: s.name, color: s.color, is_closed: s.is_closed }))) !==
    JSON.stringify(selected.stages.map(s => ({ name: s.name, color: s.color, is_closed: s.is_closed })))
  );

  if (loading) return <div style={{ color: t.textMuted, padding: 40, textAlign: "center", fontFamily: font.body }}>Loading pipelines...</div>;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: isCompact ? "1fr" : "280px minmax(0, 1fr)",
        gap: isMobile ? 12 : 20,
        minHeight: isCompact ? "auto" : 500,
      }}>
        {/* Pipeline List */}
        <Panel padding={isMobile ? 12 : 16}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Pipelines</div>
            <div
              onClick={() => { setAddingNew(true); setTimeout(() => nameInputRef.current?.focus(), 50); }}
              style={{
                width: 26, height: 26, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", background: t.emeraldLight, color: t.emerald, fontSize: 16, fontWeight: 700,
                transition: "all 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = t.emerald + "30"}
              onMouseLeave={e => e.currentTarget.style.background = t.emeraldLight}
            >+</div>
          </div>

          {addingNew && (
            <div style={{ marginBottom: 10, display: "flex", gap: 6 }}>
              <input
                ref={nameInputRef}
                value={newPipelineName}
                onChange={e => setNewPipelineName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleCreatePipeline(); if (e.key === "Escape") { setAddingNew(false); setNewPipelineName(""); } }}
                placeholder="Pipeline name..."
                style={{
                  flex: 1, minWidth: 0, background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 6,
                  padding: "6px 10px", fontSize: 12, color: t.textPrimary, outline: "none", fontFamily: font.body,
                  boxSizing: "border-box",
                }}
              />
              <Button variant="primary" size="sm" onClick={handleCreatePipeline} disabled={!newPipelineName.trim()}>Add</Button>
            </div>
          )}

          {/* On compact: horizontal scroll list; on desktop: vertical list */}
          {isCompact ? (
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
              {pipelines.map(p => (
                <div
                  key={p.id}
                  onClick={() => selectPipeline(p)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "8px 14px", borderRadius: 8, cursor: "pointer",
                    background: selectedId === p.id ? t.emeraldLight : t.surfaceWarm,
                    border: `1.5px solid ${selectedId === p.id ? t.emerald + "30" : t.border}`,
                    transition: "all 0.15s", whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{p.name}</div>
                  <span style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono }}>{p.stages.length}</span>
                  {pipelines.length > 1 && (
                    <div
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(p); }}
                      style={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 4, opacity: 0.4, flexShrink: 0 }}
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              {pipelines.map(p => (
                <div
                  key={p.id}
                  onClick={() => selectPipeline(p)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 12px", borderRadius: 8, marginBottom: 4, cursor: "pointer",
                    background: selectedId === p.id ? t.emeraldLight : "transparent",
                    border: `1.5px solid ${selectedId === p.id ? t.emerald + "30" : "transparent"}`,
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (selectedId !== p.id) e.currentTarget.style.background = t.surfaceWarm; }}
                  onMouseLeave={e => { if (selectedId !== p.id) e.currentTarget.style.background = "transparent"; }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono }}>{p.stages.length} stages</div>
                  </div>
                  {pipelines.length > 1 && (
                    <div
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(p); }}
                      style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 4, opacity: 0.5 }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = 1; e.currentTarget.style.background = t.coralLight; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = 0.5; e.currentTarget.style.background = "transparent"; }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.coral} strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {pipelines.length === 0 && !addingNew && (
            <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
              No pipelines yet. Click + to create one.
            </div>
          )}
        </Panel>

        {/* Stage Editor */}
        <Panel padding={isMobile ? 12 : 20} style={{ minWidth: 0, overflow: "hidden" }}>
          {selected ? (
            <>
              {/* Header: pipeline name + buttons */}
              <div style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                alignItems: isMobile ? "stretch" : "center",
                gap: isMobile ? 10 : 0,
                marginBottom: 16,
              }}>
                <div style={{ flex: 1, marginRight: isMobile ? 0 : 12 }}>
                  <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 4, fontFamily: font.mono }}>PIPELINE NAME</div>
                  <input
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    style={{
                      width: "100%", background: t.surfaceWarm, border: `1.5px solid ${t.border}`, borderRadius: 8,
                      padding: "8px 12px", fontSize: 14, fontWeight: 600, color: t.textPrimary, outline: "none", fontFamily: font.body,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{
                  display: "flex", gap: 8,
                  alignItems: isMobile ? "stretch" : "flex-end",
                  paddingTop: isMobile ? 0 : 18,
                }}>
                  <Button variant="outline" size="sm" onClick={handleAddStage} style={isMobile ? { flex: 1 } : undefined}>+ Stage</Button>
                  <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasChanges} style={isMobile ? { flex: 1 } : undefined}>
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>

              <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 8, fontFamily: font.mono }}>
                STAGES ({editingStages?.length || 0})
              </div>

              {editingStages && editingStages.map((stage, i) => (
                <StageRow
                  key={stage.id || `new-${i}`}
                  stage={stage}
                  index={i}
                  total={editingStages.length}
                  onUpdate={(updated) => handleUpdateStage(i, updated)}
                  onRemove={() => handleRemoveStage(i)}
                  onMoveUp={() => handleMoveStage(i, i - 1)}
                  onMoveDown={() => handleMoveStage(i, i + 1)}
                  compact={isMobile}
                  dragHandlers={{
                    onDragStart: handleStageDragStart(i),
                    onDragOver: handleStageDragOver(i),
                    onDragEnd: handleStageDragEnd,
                  }}
                />
              ))}

              {hasChanges && (
                <div style={{ marginTop: 12, padding: "8px 12px", background: t.amberLight, borderRadius: 8, border: `1px solid ${t.amber}30`, fontSize: 11, color: t.amber, fontFamily: font.body }}>
                  You have unsaved changes
                </div>
              )}

              {/* Preview */}
              <div style={{ marginTop: 20, borderTop: `1px solid ${t.border}`, paddingTop: 16 }}>
                <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, letterSpacing: 1, marginBottom: 10, fontFamily: font.mono }}>PREVIEW</div>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? `repeat(${Math.min((editingStages || []).length, 3)}, 1fr)`
                    : `repeat(${(editingStages || []).length}, 1fr)`,
                  gap: 6,
                  overflowX: "auto",
                  paddingBottom: 4,
                  WebkitOverflowScrolling: "touch",
                }}>
                  {(editingStages || []).map((stage, i) => (
                    <div key={i} style={{
                      minWidth: isMobile ? 0 : 80, background: t.surfaceWarm,
                      border: `1.5px solid ${t.border}`, borderRadius: 8,
                      padding: isMobile ? 8 : 10, textAlign: "center",
                    }}>
                      <div style={{
                        width: "100%", height: 3, borderRadius: 2, background: stage.color || "#00a878", marginBottom: isMobile ? 4 : 8,
                      }} />
                      <div style={{
                        fontSize: isMobile ? 7 : 9, fontWeight: 700, color: stage.color || "#00a878",
                        letterSpacing: 0.5, fontFamily: font.mono,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {(stage.name || "Untitled").toUpperCase()}
                      </div>
                      {stage.is_closed && (
                        <div style={{ fontSize: 7, color: t.textMuted, marginTop: 2, fontFamily: font.mono }}>CLOSED</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ padding: 40, textAlign: "center", fontSize: 13, color: t.textMuted, fontFamily: font.body }}>
              Select a pipeline to edit its stages
            </div>
          )}
        </Panel>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title="Delete Pipeline"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This will remove all stages. Deals using this pipeline will be unlinked.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => handleDeletePipeline(confirmDelete.id)}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: "12px 20px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          animation: "fadeSlide 0.3s ease",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "auto",
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}
